
import React from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Star, GraduationCap, Briefcase } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface AlumniData {
  id: string;
  title?: string;
  company?: string;
  school?: string;
  years_experience?: number;
  expertise?: string;
  profiles?: {
    first_name?: string;
    last_name?: string;
    avatar_url?: string;
  };
  services?: {
    id: string;
    title: string;
    price: number;
  }[];
  // For when we're using formatted data
  name?: string;
  avatar_url?: string;
}

interface AlumniCardProps {
  alumni: AlumniData;
  showPricing?: boolean;
}

const AlumniCard = ({ alumni, showPricing = false }: AlumniCardProps) => {
  // Handle both data structures (direct from DB or pre-formatted)
  const fullName = alumni.name || (alumni.profiles ? `${alumni.profiles.first_name || ''} ${alumni.profiles.last_name || ''}`.trim() : '');
  
  const initials = fullName
    .split(' ')
    .map(name => name && name[0])
    .filter(Boolean)
    .join('')
    .toUpperCase();

  // Get avatar URL from either format
  const avatarUrl = alumni.avatar_url || (alumni.profiles ? alumni.profiles.avatar_url : null);

  // Extract expertise tags for display
  const expertiseTags = alumni.expertise 
    ? alumni.expertise.split(',').map(tag => tag.trim()).slice(0, 2)
    : [];
    
  return (
    <Link to={`/alumni/${alumni.id}`} state={{ alumniData: alumni }} className="block h-full">
      <Card className="h-full hover:shadow-md transition-shadow duration-200 border border-gray-200 bg-white hover:scale-[1.02]">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Avatar className="h-12 w-12">
              <AvatarImage src={avatarUrl || ""} />
              <AvatarFallback className="bg-crimson text-white">
                {initials || "A"}
              </AvatarFallback>
            </Avatar>
            
            <div>
              <h3 className="font-semibold text-lg">{fullName || "Alumni"}</h3>
              <p className="text-gray-500 text-sm">{alumni.title}</p>
            </div>
          </div>
          
          <div className="mt-4">
            {alumni.company && (
              <div className="flex items-center gap-2">
                <Briefcase className="w-4 h-4 text-crimson" />
                <p className="text-sm">{alumni.company}</p>
              </div>
            )}
            
            {alumni.school && (
              <div className="flex items-center gap-2 mt-1.5">
                <GraduationCap className="w-4 h-4 text-crimson" />
                <p className="text-sm">{alumni.school}</p>
              </div>
            )}
            
            {alumni.years_experience && (
              <div className="flex items-center gap-2 mt-1.5">
                <div className="w-4 h-4 rounded-full bg-gray-200 flex-shrink-0"></div>
                <p className="text-sm">{alumni.years_experience} Years Experience</p>
              </div>
            )}
          </div>
          
          <div className="mt-5 pt-4 border-t border-gray-100">
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <Star className="h-4 w-4 text-yellow-500 mr-1" />
                <span className="text-sm font-medium">4.8</span>
              </div>
              {showPricing && alumni.services && alumni.services.length > 0 && (
                <span className="text-sm font-medium text-crimson">
                  From ${Math.min(...alumni.services.map(s => s.price))}
                </span>
              )}
            </div>
            
            <div className="mt-3 flex flex-wrap gap-2">
              {expertiseTags.map((tag, index) => (
                <Badge 
                  key={index} 
                  variant="outline"
                  className="text-xs bg-gray-100 px-2 py-1 rounded"
                >
                  {tag}
                </Badge>
              ))}
              
              {alumni.services?.slice(0, 2).map(service => (
                <Badge 
                  key={service.id} 
                  variant="outline"
                  className="text-xs bg-gray-100 px-2 py-1 rounded"
                >
                  {service.title}
                </Badge>
              ))}
              
              {(expertiseTags.length + (alumni.services?.length || 0)) > 4 && (
                <Badge 
                  variant="outline"
                  className="text-xs bg-gray-100 px-2 py-1 rounded"
                >
                  +more
                </Badge>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

export default AlumniCard;
